/**
 * V2 source path: customer/midas/components/v2/ChatBotMessageV2.tsx
 * V1 source: shared/chatBot/components/ChatBotMessage.tsx
 * Strategy: logic-copied / JSX-rewritten-in-Tailwind
 * Verified no-MUI: yes
 * Phase: 1 (+ 1.5 list-ref bracket-strip fix, + 1.5-FIX3 in-cell list-ref renderer)
 *
 * Copied from V1 verbatim (logic only):
 *   - `preprocessMarkdown` (escape sequences, Unicode bullets, bold-header → list)
 *   - SR citation chip click flow (G17 / MC-22) — ServiceReport API resolve,
 *     viewedSrs Set, openArtifact("sr_detail"), 403/404 toast handling.
 *   - SEC-REQ-5 URL validation (relative path only) before openArtifact.
 *   - SEC-REQ-7 plain-text artifact title rendering.
 *   - Source card + lightbox flow (image sources via authenticated proxy).
 *
 * Phase 1.5-FIX3 — in-cell list-ref rendering (replaces segment-splitting):
 *   The previous segment-splitter (`parseListRefs` + per-segment ReactMarkdown)
 *   shattered the input around every `<list-ref>` tag. When a `<list-ref>` lived
 *   inside a markdown pipe-table cell, the table fragment got split mid-row and
 *   none of the fragments were valid GFM tables — so the wire-trace answer
 *   collapsed into a chip-soup wall instead of a clean `<table>`. Bot team's
 *   2026-04-27 Phase 1.6 pushback called this out as a renderer bug, not a
 *   markup bug. They were right.
 *
 *   New approach: convert both `<list-ref ...>LABEL</list-ref>` AND the bot's
 *   `[[list:NNN]]` shorthand into a unicode-bracket sentinel
 *   `⟦LISTREF|page|listId|name|label⟧` BEFORE markdown parsing. ReactMarkdown
 *   leaves the sentinel as text inside whatever node it lives in (table cell,
 *   paragraph, list item — doesn't matter). The component overrides
 *   (`p`, `td`, `th`, `li`, `strong`, `em`, `a`) walk their children, find
 *   sentinels in text nodes, and substitute `<ChatListRefV2>` elements
 *   in-place — table structure preserved, G17 invariant preserved, both
 *   list-ref syntaxes supported.
 *
 * Visual layer rewritten in Tailwind:
 *   - Editorial prose-on-ground (no bubble) — matches Round 4 Paper spec.
 *   - "Midas" byline Sora 12-14px medium + JBM mono caption
 *     `REPLIED · HH:MM · N SOURCES` (no Fraunces).
 *   - Naked tables with `TBL · 01` mono caption + bold heading + right-side
 *     OPEN IN PANEL · COPY mono actions (markdown table component).
 *
 * Reuses V1 helpers (will be migrated in later phases — Phase 1 scope is the
 * chat area only): ChatSrCitationChip, ChatActionButtons,
 * MidasActionCardWrapper, ChatContextSwitchButtons, MidasArtifactThumbnail
 * (lazy), MidasLogo, ImageGalleryLightbox, ChatSourceCard.
 */
import React, {
  lazy,
  Suspense,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ClipboardList, Copy, ExternalLink } from "lucide-react";

import {
  ChatMessage,
  ChatSource,
  ChatArtifact,
  SrCitation,
  DiagramOverlayArtifactData,
} from "../../../../shared/chatBot/models/chatBotModels";
import ChatListRefV2 from "./ChatListRefV2";
import ChatSrCitationChip from "../../../../shared/chatBot/components/ChatSrCitationChip";
import ChatSuggestionChips from "../../../../shared/chatBot/components/ChatSuggestionChips";
import ChatActionButtons from "../../../../shared/chatBot/components/ChatActionButtons";
import MidasActionCardWrapper from "../../../../shared/chatBot/components/MidasActionCardWrapper";
import ChatContextSwitchButtons from "../../../../shared/chatBot/components/ChatContextSwitchButtons";
import ChatSourceCard from "../../../../shared/chatBot/components/ChatSourceCard";
import { ImageGalleryLightbox } from "../../../../shared/components/gallery";
import type { GalleryImage } from "../../../../shared/components/gallery";
import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import { openArtifact, openArtifactAtPage } from "../../midasPageSlice";
import customerMachineServiceReportsApi from "../../../machinery/api/customerMachineServiceReportsApi";
import useTranslation from "../../../../shared/hooks/useTranslation";
import MidasLogo from "../MidasLogo";
import MessageActionsV2 from "./MessageActionsV2";

// Lazy-load V1 thumbnail (V1 module — ok for Phase 1 transitive use; Phase 2
// replaces this with `MidasArtifactThumbnailV2`).
const MidasArtifactThumbnail = lazy(
  () => import("../MidasArtifactThumbnail")
);

// ═══════════════════════════════════════════════════════════════
//  Markdown pre-processing — copied verbatim from V1
// ═══════════════════════════════════════════════════════════════

const isBoldHeaderLine = (line: string): boolean =>
  /^\*\*.+\*\*$/.test(line.trim());
const isAlreadyListItem = (line: string): boolean =>
  /^\s*[-*]\s+/.test(line) || /^\s*\d+\.\s+/.test(line);
const isMarkdownHeading = (line: string): boolean =>
  /^#{1,6}\s+/.test(line.trim());

const preprocessMarkdown = (text: string): string => {
  if (!text) return "";
  let processed = text.split("\\r\\n").join("\n").split("\\n").join("\n");
  processed = processed.replace(/^[•●◦▪▸►]\s*/gm, "- ");
  const lines = processed.split("\n");
  const result: string[] = [];
  let afterBoldHeader = false;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    if (isBoldHeaderLine(trimmed)) {
      afterBoldHeader = true;
      result.push(line);
    } else if (afterBoldHeader && trimmed.length > 0) {
      if (isAlreadyListItem(trimmed) || isMarkdownHeading(trimmed)) {
        result.push(line);
      } else {
        result.push(`- ${trimmed}`);
      }
    } else {
      if (trimmed.length === 0) afterBoldHeader = false;
      result.push(line);
    }
  }
  return result.join("\n");
};

// ═══════════════════════════════════════════════════════════════
//  Phase 1.5-FIX3 — sentinel transform for list references.
//
//  Both `<list-ref id="X" page="Y" name="Z">LABEL</list-ref>` (current bot
//  output) and `[[list:NNN]]` (bot team's preferred shorthand for
//  table-cell-density references) are rewritten to a single unicode-bracket
//  sentinel that ReactMarkdown preserves as text inside any block node:
//
//    ⟦LISTREF<US><page><US><listId><US><encoded name><US><encoded label>⟧
//
//  where <US> is U+001F (Unit Separator). We CANNOT use `|` as the inner
//  separator because that's the markdown pipe-table cell separator —
//  putting `|` inside a `<td>`-bound sentinel would shatter the table again
//  (the very bug we are fixing). U+001F never appears in bot prose and is
//  invisible if it ever leaks to the DOM.
//
//  Encoding rules:
//    - `name` and `label` URL-encode (`encodeURIComponent`) so the closing
//      bracket `⟧` and `<US>` cannot occur inside.
//    - `page` is a positive integer (validated; sentinel skipped if NaN).
//    - `listId` is alphanumeric — URL-encoded as a defensive measure.
//
//  The component overrides walk children of every leaf-ish node and replace
//  any sentinel in a text node with a `<ChatListRefV2>` element. Surrounding
//  text is preserved; the original text node is split into [text, chip,
//  text, chip, ...] React children.
//
//  This replaces the earlier segment-splitter approach that shattered tables.
// ═══════════════════════════════════════════════════════════════

/** Unicode mathematical white square brackets — never appear in bot prose. */
const SENTINEL_OPEN = "⟦"; // ⟦
const SENTINEL_CLOSE = "⟧"; // ⟧
/** ASCII Unit Separator (U+001F) — non-printing, never in bot prose, not `|`. */
const SENTINEL_SEP = "";

/** Matches a complete `<list-ref ...>label</list-ref>` tag with any attribute order. */
const LIST_REF_TAG_RE = /<list-ref\b([^>]*)>([\s\S]*?)<\/list-ref>/gi;

/** Matches `[[list:NNN]]` shorthand. NNN is digits (zero-padded ok). */
const LIST_DOUBLE_BRACKET_RE = /\[\[list:(\d+)\]\]/gi;

/** Read a quoted or unquoted attribute from an attribute-string blob. */
const readAttr = (attrs: string, name: string): string | undefined => {
  const re = new RegExp(
    `\\b${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s>]+))`,
    "i"
  );
  const m = attrs.match(re);
  if (!m) return undefined;
  return m[1] ?? m[2] ?? m[3];
};

/** Build a sentinel string for a list-ref. Returns null if the page is not a finite positive int. */
const buildSentinel = (
  page: number,
  listId: string,
  name: string | undefined,
  label: string
): string | null => {
  if (!Number.isFinite(page) || page <= 0) return null;
  const encName = name ? encodeURIComponent(name) : "";
  const encLabel = encodeURIComponent(label);
  return `${SENTINEL_OPEN}LISTREF${SENTINEL_SEP}${page}${SENTINEL_SEP}${encodeURIComponent(
    listId || ""
  )}${SENTINEL_SEP}${encName}${SENTINEL_SEP}${encLabel}${SENTINEL_CLOSE}`;
};

/**
 * Replace bot list-ref markup with sentinels BEFORE markdown parsing.
 *
 * Handles three input forms:
 *   1. `<list-ref id="050" page="18" name="...">List 050</list-ref>`
 *   2. `[[list:050]]`  →  page=50, listId="050", label="List 050"
 *   3. `[<list-ref ...>...</list-ref>]` — strips the surrounding `[…]`
 *      wrapper that older bot prompts produced (Phase 1.5 invariant).
 *
 * Malformed `<list-ref>` tags (no page attr or empty label) fall through
 * unchanged so the user still sees the raw label; we don't drop content.
 */
const rewriteListRefsToSentinels = (text: string): string => {
  if (!text) return "";

  // 1) `<list-ref ...>LABEL</list-ref>` → sentinel (or pass-through if malformed).
  let out = text.replace(LIST_REF_TAG_RE, (full, attrs, rawLabel) => {
    const label = (rawLabel || "").trim();
    const pageStr = readAttr(attrs || "", "page");
    const listId = readAttr(attrs || "", "id") ?? "";
    const name = readAttr(attrs || "", "name");
    const page = pageStr !== undefined ? Number(pageStr) : NaN;
    const sentinel = buildSentinel(page, listId, name, label);
    return sentinel ?? full;
  });

  // 2) Strip surrounding `[…]` brackets that hug a sentinel (Phase 1.5 invariant).
  //    e.g. `[⟦LISTREF<US>18<US>050<US><US>List%20050⟧]` → `⟦…⟧`.
  out = out.replace(
    new RegExp(
      `\\[\\s*(${SENTINEL_OPEN}LISTREF${SENTINEL_SEP}[^${SENTINEL_CLOSE}]+${SENTINEL_CLOSE})\\s*\\]`,
      "g"
    ),
    "$1"
  );

  // 2b) Strip surrounding markdown backticks that hug a sentinel
  //     (MIDAS-V2-P15-BUG-ORANGE-WRAP defense 1, 2026-04-27 round 2).
  //     If the bot wraps `[[list:NNN]]` or `<list-ref>` in inline-code backticks
  //     (a "safe-render" pattern other model output has used), ReactMarkdown
  //     would otherwise emit a `<code>` element around the sentinel, which
  //     causes the chip to render inside `<code>` — semantically wrong AND a
  //     source of the orange wrap reported in `phase 1/bad4.PNG`.
  //     e.g. `` `⟦LISTREF…⟧` `` → `⟦…⟧`.
  out = out.replace(
    new RegExp(
      `\`\\s*(${SENTINEL_OPEN}LISTREF${SENTINEL_SEP}[^${SENTINEL_CLOSE}]+${SENTINEL_CLOSE})\\s*\``,
      "g"
    ),
    "$1"
  );

  // 3) `[[list:NNN]]` → sentinel. Bot team's preferred shorthand for
  //    high-density (table-cell) references. The numeric token is BOTH the
  //    page and the listId (zero-padded to 3 digits for display).
  out = out.replace(LIST_DOUBLE_BRACKET_RE, (_full, num) => {
    const page = Number(num);
    const listId = String(num).padStart(3, "0");
    const label = `List ${listId}`;
    const sentinel = buildSentinel(page, listId, undefined, label);
    return sentinel ?? `[[list:${num}]]`;
  });

  return out;
};

/** Parsed sentinel payload. */
interface SentinelPayload {
  page: number;
  listId: string;
  name?: string;
  label: string;
}

/** Match every sentinel occurrence in a string. */
const SENTINEL_RE = new RegExp(
  `${SENTINEL_OPEN}LISTREF${SENTINEL_SEP}(\\d+)${SENTINEL_SEP}([^${SENTINEL_SEP}]*)${SENTINEL_SEP}([^${SENTINEL_SEP}]*)${SENTINEL_SEP}([^${SENTINEL_CLOSE}]*)${SENTINEL_CLOSE}`,
  "g"
);

/**
 * Split a string into a sequence of plain-text runs and parsed sentinels.
 * Empty text runs are preserved (caller decides how to render them).
 */
const splitSentinelString = (
  s: string
): Array<{ kind: "text"; text: string } | { kind: "ref"; payload: SentinelPayload }> => {
  const out: Array<
    { kind: "text"; text: string } | { kind: "ref"; payload: SentinelPayload }
  > = [];
  if (!s) return out;
  let lastIndex = 0;
  SENTINEL_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = SENTINEL_RE.exec(s)) !== null) {
    if (m.index > lastIndex) {
      out.push({ kind: "text", text: s.slice(lastIndex, m.index) });
    }
    const page = Number(m[1]);
    const listId = decodeURIComponent(m[2] || "");
    const rawName = decodeURIComponent(m[3] || "");
    const label = decodeURIComponent(m[4] || "") || `List ${listId || page}`;
    out.push({
      kind: "ref",
      payload: {
        page,
        listId,
        name: rawName || undefined,
        label,
      },
    });
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < s.length) {
    out.push({ kind: "text", text: s.slice(lastIndex) });
  }
  return out;
};

/**
 * Walk React children produced by ReactMarkdown. For every string child,
 * scan for sentinels and substitute `<ChatListRefV2>` elements. Non-string
 * children pass through unchanged. The caller must supply the click handler
 * + isActive resolver so this helper stays free of component-level state.
 */
const renderChildrenWithSentinels = (
  children: React.ReactNode,
  resolveIsActive: (page: number) => boolean,
  onChipClick: (payload: { listId: string; page: number; name?: string }) => void
): React.ReactNode => {
  if (children == null) return children;
  if (typeof children === "string") {
    const parts = splitSentinelString(children);
    if (parts.length === 0) return "";
    if (parts.length === 1 && parts[0].kind === "text") return parts[0].text;
    return parts.map((p, i) => {
      if (p.kind === "text") return <React.Fragment key={i}>{p.text}</React.Fragment>;
      return (
        <ChatListRefV2
          key={i}
          listId={p.payload.listId}
          page={p.payload.page}
          name={p.payload.name}
          label={p.payload.label}
          isActive={resolveIsActive(p.payload.page)}
          onClick={onChipClick}
        />
      );
    });
  }
  if (Array.isArray(children)) {
    return children.map((c, i) => (
      <React.Fragment key={i}>
        {renderChildrenWithSentinels(c, resolveIsActive, onChipClick)}
      </React.Fragment>
    ));
  }
  return children;
};

// ═══════════════════════════════════════════════════════════════
//  Markdown styled components — Tailwind only
//
//  Built as a factory so the leaf-ish overrides (`p`, `td`, `th`, `li`,
//  `strong`, `em`, `a`, `code`) can substitute list-ref sentinels with
//  `<ChatListRefV2>` chips. Block container overrides (`table`, `thead`,
//  `tbody`, `tr`, `ul`, `ol`, `h1-3`, `hr`) pass children straight through.
// ═══════════════════════════════════════════════════════════════

const buildMarkdownComponents = (
  resolveIsActive: (page: number) => boolean,
  onChipClick: (payload: { listId: string; page: number; name?: string }) => void
): object => {
  const renderText = (children: React.ReactNode) =>
    renderChildrenWithSentinels(children, resolveIsActive, onChipClick);

  return {
    p: ({ children }: any) => (
      <p className="my-0 mb-2 font-sora text-[14px] font-normal leading-[1.6] text-midas-graphite last:mb-0">
        {renderText(children)}
      </p>
    ),
    strong: ({ children }: any) => (
      <strong className="font-semibold">{renderText(children)}</strong>
    ),
    em: ({ children }: any) => <em className="italic">{renderText(children)}</em>,
    h1: ({ children }: any) => (
      <h1 className="mb-1.5 mt-3 font-sora text-[16px] font-bold leading-[1.4] text-midas-graphite">
        {renderText(children)}
      </h1>
    ),
    h2: ({ children }: any) => (
      <h2 className="mb-1.5 mt-3 font-sora text-[15px] font-bold leading-[1.4] text-midas-graphite">
        {renderText(children)}
      </h2>
    ),
    h3: ({ children }: any) => (
      <h3 className="mb-1.5 mt-3 font-sora text-[14px] font-bold leading-[1.4] text-midas-graphite">
        {renderText(children)}
      </h3>
    ),
    a: ({ href, children }: any) => (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-midas-terracotta no-underline hover:underline"
      >
        {renderText(children)}
      </a>
    ),
    ul: ({ children }: any) => (
      <ul className="my-1 list-disc pl-4 font-sora text-[13px] marker:text-midas-terracotta">
        {children}
      </ul>
    ),
    ol: ({ children }: any) => (
      <ol className="my-1 list-decimal pl-4 font-sora text-[13px]">{children}</ol>
    ),
    li: ({ children }: any) => (
      <li className="my-1 leading-[1.5]">{renderText(children)}</li>
    ),
    code: ({ inline, children, className }: any) => {
      if (inline) {
        // MIDAS-V2-P15-BUG-ORANGE-WRAP defense 2 (2026-04-27 round 2):
        // If the inline code contains ONLY a sentinel (with optional
        // surrounding whitespace), unwrap the `<code>` entirely and render
        // the chip directly. The chip is interactive UI, not source code —
        // nesting `<button>` inside `<code>` is semantically wrong and is
        // a source of the orange wrap reported in `phase 1/bad4.PNG`.
        // Plain inline code (e.g. `Sheet 070`) still renders as `<code>`.
        if (typeof children === "string") {
          const trimmed = children.trim();
          if (
            trimmed.startsWith(SENTINEL_OPEN + "LISTREF") &&
            trimmed.endsWith(SENTINEL_CLOSE)
          ) {
            // Verify the entire trimmed string is one or more sentinels
            // (no other text). If so, drop the `<code>` wrapper.
            const stripped = trimmed.replace(SENTINEL_RE, "");
            if (stripped.trim() === "") {
              return <>{renderText(children)}</>;
            }
          }
        } else if (Array.isArray(children) && children.length === 1 && typeof children[0] === "string") {
          const trimmed = children[0].trim();
          if (
            trimmed.startsWith(SENTINEL_OPEN + "LISTREF") &&
            trimmed.endsWith(SENTINEL_CLOSE)
          ) {
            const stripped = trimmed.replace(SENTINEL_RE, "");
            if (stripped.trim() === "") {
              return <>{renderText(children)}</>;
            }
          }
        }
        return (
          <code className="font-mono text-[14px]" style={{ color: "#3A332C" }}>
            {renderText(children)}
          </code>
        );
      }
      return (
        <pre className="my-2 max-h-[200px] overflow-auto rounded-lg border border-midas-stone bg-midas-bone-light p-3 font-mono text-[12px]">
          <code className={className}>{children}</code>
        </pre>
      );
    },
    table: ({ children }: any) => (
      <div className="my-3 overflow-x-auto">
        <table className="w-full border-collapse font-sora text-[13px]">
          {children}
        </table>
      </div>
    ),
    thead: ({ children }: any) => (
      <thead className="border-b border-midas-stone">{children}</thead>
    ),
    th: ({ children }: any) => (
      <th className="px-2.5 py-2 text-left font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-midas-muted-stone">
        {renderText(children)}
      </th>
    ),
    td: ({ children }: any) => (
      <td className="border-b border-midas-stone/40 px-2.5 py-2 align-top text-midas-graphite">
        {renderText(children)}
      </td>
    ),
    hr: () => <hr className="my-3 border-t border-midas-stone" />,
  };
};

// ═══════════════════════════════════════════════════════════════
//  Artifact badge icons (lucide)
// ═══════════════════════════════════════════════════════════════

const ARTIFACT_BADGE_ICONS: Record<string, React.ElementType> = {
  table: ClipboardList,
  machine_card: ClipboardList,
  sr_summary: ClipboardList,
  image: ClipboardList,
  platform_file: ClipboardList,
};

// ═══════════════════════════════════════════════════════════════
//  ChatBotMessageV2
// ═══════════════════════════════════════════════════════════════

interface ChatBotMessageV2Props {
  message: ChatMessage;
  showAvatar?: boolean;
  showTimestamp?: boolean;
  onSuggestionClick?: (text: string) => void;
  isPhone?: boolean;
  /** Last message in this assistant turn — only show terminal action row on the last */
  isLastInGroup?: boolean;
}

const isPlatformMode = (intent?: string): boolean => {
  if (!intent) return false;
  return intent.startsWith("PLATFORM_");
};

const ChatBotMessageV2: React.FC<ChatBotMessageV2Props> = ({
  message,
  showAvatar = false,
  showTimestamp = false,
  onSuggestionClick,
  isPhone = false,
  isLastInGroup = false,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isOnMidasPage =
    location.pathname === "/midas" || location.pathname === "/midas-v2";
  const { translate } = useTranslation("chatBot");

  // MC-22: active SR / page selectors (copied verbatim from V1)
  const artifactTabs = useAppSelector((s) => s.midasPage.artifactTabs);
  const activeArtifactTabId = useAppSelector(
    (s) => s.midasPage.activeArtifactTabId
  );
  const activeSrReportNumber = useMemo(() => {
    const active = artifactTabs.find((t) => t.id === activeArtifactTabId);
    if (!active) return null;
    if (active.artifact.type !== "sr_detail") return null;
    const data = (active.artifact.data || {}) as { reportNumber?: string };
    return data.reportNumber || null;
  }, [artifactTabs, activeArtifactTabId]);

  const activeRefPage = useMemo(() => {
    const active = artifactTabs.find((t) => t.id === activeArtifactTabId);
    if (!active) return null;
    if (active.artifact.source !== "cross_reference") return null;
    const data = (active.artifact.data || {}) as DiagramOverlayArtifactData;
    return typeof data.page === "number" ? data.page : (active.artifact.page ?? null);
  }, [artifactTabs, activeArtifactTabId]);

  // Phase 1.5-FIX3 — viewedRefPages dropped (Style A in `ChatListRefV2` ignores
  // `isViewed`). Click handler is unchanged otherwise: dispatches
  // `openArtifactAtPage` (G17 invariant) and opens the matching cross_reference
  // artifact tab when one is present in the message's artifacts list.
  const handleListRefClick = useCallback(
    (payload: { listId: string; page: number; name?: string }) => {
      dispatch(openArtifactAtPage({ page: payload.page }));
      const matching = (message.artifacts || []).find((a) => {
        if (a.source !== "cross_reference") return false;
        const data = (a.data || {}) as DiagramOverlayArtifactData;
        const dataPage = typeof data.page === "number" ? data.page : a.page;
        return dataPage === payload.page;
      });
      if (matching) {
        if (matching.url && !matching.url.startsWith("/") && matching.url !== "")
          return;
        dispatch(
          openArtifact({
            type: matching.type as any,
            title: matching.title,
            source: matching.source,
            url: matching.url || "",
            document_id: matching.document_id,
            page: matching.page,
            mime_type: matching.mime_type,
            entity_type: matching.entity_type,
            entity_id: matching.entity_id,
            file_id: matching.file_id,
            data: matching.data as any,
          })
        );
      }
    },
    [dispatch, message.artifacts]
  );

  // Memoise the markdown component factory so React doesn't recreate it
  // on every keystroke. New identity only when the active page or click
  // handler change.
  const markdownComponents = useMemo(
    () =>
      buildMarkdownComponents(
        (page: number) => activeRefPage === page,
        handleListRefClick
      ),
    [activeRefPage, handleListRefClick]
  );

  // MC-22 chip click state
  const [resolvingSr, setResolvingSr] = useState<string | null>(null);
  const [viewedSrs, setViewedSrs] = useState<Set<string>>(new Set());
  const [toast, setToast] = useState<{
    message: string;
    severity: "error" | "warning";
  } | null>(null);

  const handleSrChipClick = useCallback(
    async (citation: SrCitation) => {
      if (resolvingSr) return;
      const reportNumber = citation.reportNumber;
      setResolvingSr(reportNumber);
      try {
        const sr = await customerMachineServiceReportsApi.getServiceReport(
          reportNumber
        );
        const machineId = (sr as any).machineId;
        if (!machineId) {
          setToast({
            message: translate("srCitations.errorGeneric"),
            severity: "error",
          });
          return;
        }
        setViewedSrs((prev) => {
          const next = new Set(prev);
          next.add(reportNumber);
          return next;
        });
        if (isOnMidasPage) {
          dispatch(
            openArtifact({
              type: "sr_detail",
              title: reportNumber,
              source: "machineid",
              url: "",
              entity_type: "service_report",
              entity_id: reportNumber,
              data: { reportNumber, machineId },
            })
          );
        } else {
          navigate(
            `/machinery/${machineId}/service-reports/${reportNumber}`
          );
        }
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 403) {
          setToast({
            message: translate("srCitations.errorForbidden"),
            severity: "warning",
          });
        } else if (status === 404) {
          setToast({
            message: translate("srCitations.errorNotFound"),
            severity: "warning",
          });
        } else {
          setToast({
            message: translate("srCitations.errorGeneric"),
            severity: "error",
          });
        }
      } finally {
        setResolvingSr(null);
      }
    },
    [resolvingSr, isOnMidasPage, dispatch, navigate, translate]
  );

  const showSources =
    !isPlatformMode(message.intent) &&
    message.sources &&
    message.sources.length > 0;

  const sourcesCount = (message.sources || []).length;

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const blobUrlsRef = useRef<Map<number, string>>(new Map());

  const handleSourceImageLoaded = useCallback(
    (index: number, blobUrl: string) => {
      blobUrlsRef.current.set(index, blobUrl);
    },
    []
  );

  const openSourceAsArtifact = useCallback(
    (source: ChatSource) => {
      if (!source.imageUrl) return;
      if (!source.imageUrl.startsWith("/")) return;
      dispatch(
        openArtifact({
          type: "image",
          title:
            source.document + (source.page ? ` p.${source.page}` : ""),
          source: "bot",
          url: source.imageUrl,
          document_id: source.document,
          page: source.page,
        })
      );
    },
    [dispatch]
  );

  const handleArtifactBadgeClick = useCallback(
    (artifact: ChatArtifact) => {
      if (artifact.url && !artifact.url.startsWith("/") && artifact.url !== "")
        return;
      dispatch(
        openArtifact({
          type: artifact.type as any,
          title: artifact.title,
          source: artifact.source || "bot",
          url: artifact.url || "",
          document_id: artifact.document_id,
          page: artifact.page,
          mime_type: artifact.mime_type,
          entity_type: artifact.entity_type,
          entity_id: artifact.entity_id,
          file_id: artifact.file_id,
          data: artifact.data as any,
        })
      );
    },
    [dispatch]
  );

  const richArtifacts = (message.artifacts || []).filter(
    (a) =>
      a.type === "table" || a.type === "machine_card" || a.type === "sr_summary"
  );
  const allArtifacts = isOnMidasPage ? message.artifacts || [] : richArtifacts;

  const handleSourceImageClick = useCallback(
    (clickedBlobUrl: string) => {
      if (isOnMidasPage) {
        const entries = Array.from(blobUrlsRef.current.entries()).sort(
          ([a], [b]) => a - b
        );
        const match = entries.find(([, url]) => url === clickedBlobUrl);
        if (match && message.sources) {
          const source = message.sources[match[0]];
          if (source) {
            openSourceAsArtifact(source);
            return;
          }
        }
      }
      const entries = Array.from(blobUrlsRef.current.entries()).sort(
        ([a], [b]) => a - b
      );
      const galleryIdx = entries.findIndex(([, url]) => url === clickedBlobUrl);
      setLightboxIndex(galleryIdx >= 0 ? galleryIdx : 0);
      setLightboxOpen(true);
    },
    [isOnMidasPage, message.sources, openSourceAsArtifact]
  );

  const getLightboxImages = useCallback((): GalleryImage[] => {
    const entries = Array.from(blobUrlsRef.current.entries()).sort(
      ([a], [b]) => a - b
    );
    return entries.map(([idx, url]) => {
      const src = message.sources?.[idx];
      return {
        id: `source-${idx}`,
        src: url,
        alt: src?.document
          ? `${src.document} — Page ${src.page || ""}`
          : `Source ${idx + 1}`,
        name: src?.document || `source-image-${idx + 1}`,
      };
    });
  }, [message.sources]);

  const formatTime = (ts: string) => {
    const d = new Date(ts);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="relative animate-in fade-in slide-in-from-bottom-1 duration-200">
      {/* Avatar (only on first in group) */}
      {showAvatar && (
        <div className="absolute left-0 top-0">
          <MidasLogo size="sm" animated="none" />
        </div>
      )}

      {/* Byline + caption row */}
      {showAvatar && (
        <div className="ml-9 mb-2 flex flex-wrap items-baseline gap-2">
          <span
            className="font-sora text-[14px] font-medium text-midas-graphite"
            style={{ fontFamily: "'Sora', sans-serif" }}
          >
            Midas
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-midas-muted-stone">
            replied · {formatTime(message.timestamp)}
            {showSources ? ` · ${sourcesCount} source${sourcesCount === 1 ? "" : "s"}` : ""}
          </span>
        </div>
      )}

      {/* Body — editorial prose-on-ground (no bubble).
          Phase 1.5-FIX3 — single ReactMarkdown invocation with sentinel-based
          list-ref substitution. Pipe tables containing list-refs render as
          real `<table>` elements; chips appear inside the cells. */}
      <div className="ml-9 max-w-none">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={markdownComponents}
        >
          {preprocessMarkdown(rewriteListRefsToSentinels(message.text))}
        </ReactMarkdown>

        {/* Inline rich artifacts (V1 thumbnails — Phase 2 will replace) */}
        {allArtifacts.length > 0 &&
          (isOnMidasPage ? (
            <div className="mt-3 flex flex-wrap gap-2 max-[600px]:[&>*]:max-w-[calc(50%-4px)]">
              <Suspense fallback={null}>
                {allArtifacts.slice(0, 5).map((artifact, idx) => (
                  <MidasArtifactThumbnail key={idx} artifact={artifact} />
                ))}
              </Suspense>
            </div>
          ) : (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {allArtifacts.map((artifact, idx) => {
                const Icon = ARTIFACT_BADGE_ICONS[artifact.type] || ClipboardList;
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => handleArtifactBadgeClick(artifact)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-midas-terracotta/30 bg-midas-bone-light px-2.5 py-1 text-[11px] font-semibold text-midas-graphite transition-colors hover:border-midas-terracotta hover:bg-midas-terracotta-soft"
                  >
                    <Icon
                      className="h-3.5 w-3.5 text-midas-terracotta"
                      strokeWidth={1.75}
                    />
                    <span className="max-w-[140px] truncate">
                      {/* SEC-REQ-7: plain text only */}
                      {artifact.title}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}

        {/* Source cards (machine mode only — hidden on /midas-v2 since
            the artifact panel takes over) */}
        {showSources && !isOnMidasPage && (() => {
          const sources = message.sources!;
          const imageSources = sources
            .map((s, i) => ({ source: s, idx: i }))
            .filter(({ source }) => !!source.imageUrl);
          const textSources = sources
            .map((s, i) => ({ source: s, idx: i }))
            .filter(({ source }) => !source.imageUrl);
          const extraImageCount =
            imageSources.length > 1 ? imageSources.length - 1 : 0;

          return (
            <div className="mt-3 flex flex-col gap-2">
              {imageSources.length > 0 && (
                <div className="flex items-stretch gap-2">
                  <ChatSourceCard
                    source={imageSources[0].source}
                    onImageClick={handleSourceImageClick}
                    onImageLoaded={(blobUrl) =>
                      handleSourceImageLoaded(imageSources[0].idx, blobUrl)
                    }
                  />
                  {extraImageCount > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        const entries = Array.from(
                          blobUrlsRef.current.entries()
                        ).sort(([a], [b]) => a - b);
                        if (entries.length > 1) {
                          setLightboxIndex(1);
                          setLightboxOpen(true);
                        }
                      }}
                      className="flex min-w-[56px] flex-col items-center justify-center rounded-lg border border-midas-terracotta/30 bg-midas-bone-light px-3 transition-colors hover:bg-midas-terracotta-soft"
                    >
                      <span className="font-mono text-[16px] font-bold text-midas-terracotta">
                        +{extraImageCount}
                      </span>
                      <span className="mt-0.5 font-sora text-[10px] text-midas-muted-stone">
                        more
                      </span>
                    </button>
                  )}
                </div>
              )}
              {imageSources.slice(1).map(({ source, idx }) => (
                <ChatSourceCard
                  key={`hidden-${idx}`}
                  source={source}
                  onImageClick={handleSourceImageClick}
                  onImageLoaded={(blobUrl) =>
                    handleSourceImageLoaded(idx, blobUrl)
                  }
                  hidden
                />
              ))}
              {textSources.length > 0 && (
                <div
                  className="flex gap-2 overflow-x-auto"
                  style={{ scrollbarWidth: "none" }}
                >
                  {textSources.map(({ source, idx }) => (
                    <ChatSourceCard key={idx} source={source} />
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* SR citation chips */}
        {message.srCitations && message.srCitations.length > 0 && (
          <div className="mt-3">
            <div className="mb-1.5 flex items-center gap-1.5">
              <ExternalLink
                className="h-3 w-3 text-midas-terracotta"
                strokeWidth={1.75}
              />
              <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-midas-muted-stone">
                {translate("srCitations.heading")}
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {message.srCitations.map((c) => (
                <ChatSrCitationChip
                  key={c.reportNumber}
                  citation={c}
                  isActive={activeSrReportNumber === c.reportNumber}
                  isViewed={viewedSrs.has(c.reportNumber)}
                  isResolving={resolvingSr === c.reportNumber}
                  onClick={handleSrChipClick}
                />
              ))}
            </div>
          </div>
        )}

        {/* Action row — terminal element of the assistant turn (Round 6 rule).
            Only on the LAST message in the assistant group. */}
        {isLastInGroup && (
          <MessageActionsV2
            messageId={message.id}
            text={message.text}
            isPhone={isPhone}
          />
        )}
      </div>

      {/* Timestamp — fallback when not shown via byline */}
      {showTimestamp && !showAvatar && (
        <div className="ml-9 mt-1 font-mono text-[10px] font-normal text-midas-muted-stone">
          {formatTime(message.timestamp)}
        </div>
      )}

      {/* Suggestion chips — hidden on /midas-v2 */}
      {!isOnMidasPage &&
        message.suggestions &&
        message.suggestions.length > 0 &&
        onSuggestionClick && (
          <ChatSuggestionChips
            suggestions={message.suggestions}
            onChipClick={onSuggestionClick}
            isPhone={isPhone}
          />
        )}

      {/* Action buttons (legacy MUI list — V1 component, Phase 1 keeps it lit) */}
      {message.actions && message.actions.length > 0 && (
        <div className="ml-9 mt-1.5">
          <ChatActionButtons messageId={message.id} actions={message.actions} />
        </div>
      )}

      {/* MA-0 Action card */}
      {message.actionRequest && (
        <MidasActionCardWrapper actionRequest={message.actionRequest} />
      )}

      {/* Tier-0 context switch chips */}
      {message.uiActionRequests && message.uiActionRequests.length > 0 && (
        <ChatContextSwitchButtons uiActions={message.uiActionRequests} />
      )}

      {/* Lightbox — used in machine chat only (not on /midas-v2) */}
      {showSources && !isOnMidasPage && (
        <ImageGalleryLightbox
          images={getLightboxImages()}
          open={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          initialIndex={lightboxIndex}
        />
      )}

      {/* SR citation toast — Tailwind-only fixed banner (no MUI Snackbar) */}
      {toast && (
        <div
          role="alert"
          className={`fixed bottom-4 left-1/2 z-50 -translate-x-1/2 rounded-md border px-4 py-2 font-sora text-[13px] shadow-lg ${
            toast.severity === "error"
              ? "border-red-300 bg-red-50 text-red-700"
              : "border-amber-300 bg-amber-50 text-amber-800"
          }`}
        >
          <div className="flex items-center gap-3">
            <span>{toast.message}</span>
            <button
              type="button"
              onClick={() => setToast(null)}
              className="font-mono text-[11px] uppercase opacity-70 hover:opacity-100"
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatBotMessageV2;
